import { PrismaEnums } from '@airreading/schema/browser';
import { z } from 'zod';

import { CoachGrade, coachGradeSchema } from '../coach-grades.js';
import { coachRegionSchema } from '../coach-region.js';
import { filteringSchemaFrom, pagingSchema, sortingSchemaFrom, sortOrderSchemaFrom } from './modifier.js';
import { paginatedResponse } from './util-schema-types.js';

/**
 * This single import eventually forms an import cycle with `users.js`.
 */
import { Temporal } from 'temporal-polyfill';
import {
  ClassEligibility,
  classEligibilitySchema,
  coachAvailabilityFromFilterSchema,
  coachAvailabilitySchema,
  coachAvailabilityTypeSchema,
} from './coach-availability-shared.js';
import {
  backgroundCheckStatusSchema,
  isCoachBasicInfoCompleted,
  teachingPreferenceSchema,
  techCheckTypeSchema,
} from './coach-shared.js';
import { classTimeTypeSchema } from './class-time-shared.js';
import { dayOfWeekSchema } from './meeting-block.js';
import { OrganizationMatchNames } from './organization-match-names.js';
import { videoConferenceTypeSchema } from './org-shared.js';
import { plainSessionLikeSchema } from './plain-session-like.js';
import { studentGroupWithClassTimeSchema } from './student-group-shared.js';
import { paymentAccountType } from './payment-account-type.js';
import { coachOverallGradeEnum, coachOverallGradeSchema } from './coach-overall-grade.js';
import { logicalOperatorSchema } from '../query-utils.js';

export const zoomTypeSchema = z.nativeEnum(PrismaEnums.ZoomType);
export type ZoomType = z.infer<typeof zoomTypeSchema>;
export const ZoomType = zoomTypeSchema.enum;

export const ZoomTypeLabelMap: Record<ZoomType, string> = {
  [ZoomType.basic]: 'Basic',
  [ZoomType.license]: 'Licensed',
  [ZoomType.permanent_license]: 'Permanent Licensed',
};

// Specific ordering of Zoom types for UI purposes, "-" represents no license selected (null)
export const zoomNoLicenseString = '-';
export type ZoomNoLicenseString = typeof zoomNoLicenseString;
export const ZoomLicenseOptions: (ZoomType | ZoomNoLicenseString)[] = [
  ZoomType.basic,
  ZoomType.license,
  ZoomType.permanent_license,
  zoomNoLicenseString,
];

export const zoomInviteStatusSchema = z.nativeEnum(PrismaEnums.ZoomInviteStatus);
export type ZoomInviteStatus = z.infer<typeof zoomInviteStatusSchema>;
export const ZoomInviteStatus = zoomInviteStatusSchema.enum;

/**
 * Although Prisma exposes CoachStatus via $Enums, it is not reliably available
 * in all environments, so it is defined manually here to ensure access.
 */
export const coachStatusSchema = z.enum(['onboarding', 'ready_to_teach', 'offboarded', 'pause']);
export type CoachStatus = z.infer<typeof coachStatusSchema>;
export const CoachStatus = coachStatusSchema.enum;
export const CoachStatusLabelMap: Record<CoachStatus, string> = {
  [CoachStatus.onboarding]: 'Onboarding',
  [CoachStatus.ready_to_teach]: 'Ready to Teach',
  [CoachStatus.offboarded]: 'Offboarded',
  [CoachStatus.pause]: 'Pause',
};

export const CoachStatusColorMap: Record<CoachStatus, string> = {
  [CoachStatus.onboarding]: '#2563EB',
  [CoachStatus.ready_to_teach]: '#22C55E',
  [CoachStatus.offboarded]: '#8E8E93',
  [CoachStatus.pause]: '#EA580C',
};

export const coachSubStatusSchema = z.enum([
  'offboard_requested',
  'teacher_requested',
  'teaching_quality_issues',
  'tech_issues',
  'absenteeism',
  'non_renewal_of_contract',
  'c_background',
  'n_background',
  'checkr_issue',
  'zoom_unresponsive',
]);
export type CoachSubStatus = z.infer<typeof coachSubStatusSchema>;
export const CoachSubStatus = coachSubStatusSchema.enum;
export const CoachSubStatusLabelMap: Record<CoachSubStatus, string> = {
  [CoachSubStatus.offboard_requested]: 'Offboard Requested',
  [CoachSubStatus.teacher_requested]: 'Teacher Requested',
  [CoachSubStatus.teaching_quality_issues]: 'Teaching Quality Issues',
  [CoachSubStatus.tech_issues]: 'Tech Issues',
  [CoachSubStatus.absenteeism]: 'Absenteeism',
  [CoachSubStatus.non_renewal_of_contract]: 'Non-Renewal of Contract',
  [CoachSubStatus.c_background]: 'C Background',
  [CoachSubStatus.n_background]: 'N Background',
  [CoachSubStatus.checkr_issue]: 'Checkr Issue',
  [CoachSubStatus.zoom_unresponsive]: 'Zoom Unresponsive',
};

export const CoachStatusTree: Record<CoachStatus, CoachSubStatus[]> = {
  [CoachStatus.onboarding]: [],
  [CoachStatus.ready_to_teach]: [],
  [CoachStatus.offboarded]: [],
  [CoachStatus.pause]: [
    CoachSubStatus.offboard_requested,
    CoachSubStatus.teacher_requested,
    CoachSubStatus.teaching_quality_issues,
    CoachSubStatus.tech_issues,
    CoachSubStatus.absenteeism,
    CoachSubStatus.non_renewal_of_contract,
    CoachSubStatus.c_background,
    CoachSubStatus.n_background,
    CoachSubStatus.checkr_issue,
    CoachSubStatus.zoom_unresponsive,
  ],
};

export type BackgroundCheckStatus = z.infer<typeof backgroundCheckStatusSchema>;
export const BackgroundCheckStatus = backgroundCheckStatusSchema.enum;
export const BackgroundCheckStatusLabelMap: Record<BackgroundCheckStatus, string> = {
  [BackgroundCheckStatus.checkr_passed]: 'Checkr passed',
  [BackgroundCheckStatus.ebr_fingerprint_passed]: 'EBR fingerprint passed',
  [BackgroundCheckStatus.ebr_fingerprint_quality_reject]: 'EBR fingerprint quality rejection',
  [BackgroundCheckStatus.california_fingerprint_passed]: 'California fingerprint passed',
  [BackgroundCheckStatus.florida_fingerprint_passed]: 'Florida fingerprint passed',
  [BackgroundCheckStatus.florida_foundations_course_completed]: 'Florida Foundations Course completed',
  [BackgroundCheckStatus.uva_cleared]: 'UVA cleared',
  [BackgroundCheckStatus.louisiana_can]: 'Louisiana CAN',
};

export const ClassEligibilityLabelMap: Record<ClassEligibility, string> = {
  [ClassEligibility.can_teach_intervention]: 'Can teach intervention',
  [ClassEligibility.can_teach_practice]: 'Can teach practice',
  [ClassEligibility.can_teach_diagnostic]: 'Can teach diagnostic',
  [ClassEligibility.air_crew]: 'Air Crew',
  [ClassEligibility.imported_from_v1]: 'Imported',
};

/** Ensures "Can teach intervention" is present; used when workshop onboarding is passed. */
export function withCanTeachInterventionClassEligibility(classEligibilities: ClassEligibility[]): ClassEligibility[] {
  if (classEligibilities.includes(ClassEligibility.can_teach_intervention)) {
    return classEligibilities;
  }
  return [...classEligibilities, ClassEligibility.can_teach_intervention];
}

/** Removes "Can teach intervention" when workshop onboarding is unchecked. */
export function withoutCanTeachInterventionClassEligibility(
  classEligibilities: ClassEligibility[],
): ClassEligibility[] {
  return classEligibilities.filter((eligibility) => eligibility !== ClassEligibility.can_teach_intervention);
}

export type CoachOnboardingCompletionState = {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  displayName: string | null;
  timeZone: string | null;
  documentsReviewPassed: boolean;
  workshopPassed: boolean;
  onboardingTechCheckPassed: boolean;
};

export function isCoachOnboardingStepsComplete(state: CoachOnboardingCompletionState): boolean {
  return (
    isCoachBasicInfoCompleted({
      firstName: state.firstName,
      lastName: state.lastName,
      phone: state.phone,
      displayName: state.displayName,
      timeZone: state.timeZone,
    }) &&
    state.documentsReviewPassed &&
    state.workshopPassed &&
    state.onboardingTechCheckPassed
  );
}

/** Promotes onboarding → ready to teach when all onboarding steps are complete. */
export function coachStatusFromOnboardingCompletion(
  currentStatus: CoachStatus | null,
  onboardingComplete: boolean,
): CoachStatus | undefined {
  if (onboardingComplete && currentStatus === CoachStatus.onboarding) {
    return CoachStatus.ready_to_teach;
  }
  return undefined;
}

export type CoachOnboardingDerivedProfileFields = {
  classEligibilities?: ClassEligibility[];
  zoomTechCheck?: boolean;
  googleMeetTechCheck?: boolean;
  status?: CoachStatus;
};

export function deriveCoachOnboardingProfileFields(input: {
  existing: CoachOnboardingCompletionState & {
    classEligibilities: ClassEligibility[];
    status: CoachStatus | null;
  };
  patch: {
    workshopPassed?: boolean;
    onboardingTechCheckPassed?: boolean;
    documentsReviewPassed?: boolean;
    classEligibilities?: ClassEligibility[];
    displayName?: string;
  };
}): CoachOnboardingDerivedProfileFields {
  const { existing, patch } = input;
  const derived: CoachOnboardingDerivedProfileFields = {};

  const baseClassEligibilities = patch.classEligibilities ?? existing.classEligibilities;
  if (patch.workshopPassed === true) {
    derived.classEligibilities = withCanTeachInterventionClassEligibility(baseClassEligibilities);
  } else if (patch.workshopPassed === false) {
    derived.classEligibilities = withoutCanTeachInterventionClassEligibility(baseClassEligibilities);
  }

  if (patch.onboardingTechCheckPassed === true) {
    derived.zoomTechCheck = true;
  } else if (patch.onboardingTechCheckPassed === false) {
    derived.zoomTechCheck = false;
  }

  const nextOnboardingState: CoachOnboardingCompletionState = {
    firstName: existing.firstName,
    lastName: existing.lastName,
    phone: existing.phone,
    displayName: patch.displayName ?? existing.displayName,
    timeZone: existing.timeZone,
    documentsReviewPassed: patch.documentsReviewPassed ?? existing.documentsReviewPassed,
    workshopPassed: patch.workshopPassed ?? existing.workshopPassed,
    onboardingTechCheckPassed: patch.onboardingTechCheckPassed ?? existing.onboardingTechCheckPassed,
  };
  const nextStatus = coachStatusFromOnboardingCompletion(
    existing.status,
    isCoachOnboardingStepsComplete(nextOnboardingState),
  );
  if (nextStatus !== undefined) {
    derived.status = nextStatus;
  }

  return derived;
}

export const coachAttendanceGradeSchema = z.enum(['A', 'B', 'C', 'D', 'S', 'Never_Taught']);
export type CoachAttendanceGrade = z.infer<typeof coachAttendanceGradeSchema>;
export const CoachAttendanceGrade = coachAttendanceGradeSchema.enum;
export const CoachAttendanceGradeLabelMap: Record<CoachAttendanceGrade, string> = {
  [CoachAttendanceGrade.A]: 'A',
  [CoachAttendanceGrade.B]: 'B',
  [CoachAttendanceGrade.C]: 'C',
  [CoachAttendanceGrade.D]: 'D',
  [CoachAttendanceGrade.S]: 'S',
  [CoachAttendanceGrade.Never_Taught]: 'Never Taught',
};
export const coachAttendanceGradeOptions = coachAttendanceGradeSchema.options;

export type CoachAttendanceGradeSelectable = CoachAttendanceGrade | 'null';

export function coachAttendanceGradeOption(value: CoachAttendanceGradeSelectable | null | undefined): string {
  if (value === 'null' || value == null) return '(empty)';
  return CoachAttendanceGradeLabelMap[value] ?? '(unknown)';
}

export const teachingGradeEnum = z.enum(['S', 'A', 'B', 'C', 'D', 'New']);
export const teachingGradeOptions = teachingGradeEnum.options;
export const TeachingGrade = teachingGradeEnum.enum;
export const teachingGradeSchema = teachingGradeEnum.nullable();
export type TeachingGrade = z.infer<typeof teachingGradeSchema>;

export const coachOverallGradeOptions = coachOverallGradeEnum.options;
export const CoachOverallGrade = coachOverallGradeEnum.enum;
export type CoachOverallGrade = z.infer<typeof coachOverallGradeSchema>;
export const CoachOverallGradeLabelMap: Record<NonNullable<CoachOverallGrade>, string> = {
  [CoachOverallGrade.A]: 'A',
  [CoachOverallGrade.B]: 'B',
  [CoachOverallGrade.C]: 'C',
  [CoachOverallGrade.D]: 'D',
  [CoachOverallGrade.S]: 'S',
  [CoachOverallGrade.Never_Taught]: 'Never Taught',
  [CoachOverallGrade.Not_reviewed]: 'Not reviewed',
};
export const CoachOverallGradeColorMap: Record<NonNullable<CoachOverallGrade>, string> = {
  [CoachOverallGrade.A]: '#FF383C',
  [CoachOverallGrade.B]: '#F97316',
  [CoachOverallGrade.C]: '#FB923C',
  [CoachOverallGrade.D]: '#EAB308',
  [CoachOverallGrade.S]: '#DC2626',
  [CoachOverallGrade.Never_Taught]: '',
  [CoachOverallGrade.Not_reviewed]: '',
};

const coachOverallGradeOrder: Record<NonNullable<CoachOverallGrade> | 'null', number> = {
  S: 5,
  A: 4,
  B: 3,
  C: 2,
  D: 1,
  Never_Taught: 0,
  Not_reviewed: 0,
  null: 0,
};

export function coachOverallGradeFrom(input: CoachGrade | CoachAttendanceGrade | null): CoachOverallGrade {
  try {
    return coachOverallGradeSchema.parse(input);
  } catch {
    return null;
  }
}

type LetterGrade = 'D' | 'C' | 'B' | 'A' | 'S';
function minLetterGrade(a: LetterGrade | null, b: LetterGrade | null): LetterGrade | null {
  const aRank = coachOverallGradeOrder[a === null ? 'null' : a];
  const bRank = coachOverallGradeOrder[b === null ? 'null' : b];
  return aRank <= bRank ? a : b;
}

/** Number of days a completed reassignment keeps counting against the attendance grade. */
export const ATTENDANCE_GRADE_REASSIGNMENT_LOOKBACK_DAYS = 90;

/**
 * Attendance grade is a system-generated metric based on how many student groups
 * were reassigned away from the teacher within the rolling lookback window.
 *
 * Teachers who have never taught a session are graded "Never Taught" without
 * evaluating reassignments at all.
 */
export function calculateAttendanceGradeFromReassignments({
  hasEverTaught,
  reassignedStudentGroupCount,
}: {
  hasEverTaught: boolean;
  reassignedStudentGroupCount: number;
}): CoachAttendanceGrade {
  if (!hasEverTaught) {
    return 'Never_Taught';
  }
  if (reassignedStudentGroupCount >= 3) {
    return 'D';
  }
  if (reassignedStudentGroupCount === 2) {
    return 'C';
  }
  if (reassignedStudentGroupCount === 1) {
    return 'B';
  }
  return 'A';
}

export function calculateOverallGrade(
  quality: CoachGrade | null,
  attendance: CoachAttendanceGrade | null,
): CoachOverallGrade {
  // Max score special case. This is the only way to get an overall grade of 'S'.
  if (quality === 'S' && attendance === 'A') {
    return 'S';
  }

  // When the attendance grade is "Never Taught", the overall grade also becomes
  // "Never Taught" unless the quality grade is at "B" or lower, in which case
  // the overall grade is equal to the quality grade.
  if (attendance === 'Never_Taught') {
    return quality === 'A' || quality === 'S' || quality === 'New' ? 'Never_Taught' : quality;
  }

  // When the quality grade is "New", the overall grade should be "Not_reviewed"
  // for any attendance grade other than "Never_Taught".
  if (quality === 'New') {
    return 'Not_reviewed';
  }

  // In all remaining scenarios, the lower grade prevails.
  return minLetterGrade(quality, attendance);
}

export const coachSchema = z.object({
  id: z.string(),
  zoomPersonalMeetingUrl: z.string().nullable(),
  zoomAccountId: z.string().nullable(),
  zoomType: zoomTypeSchema.nullable(),
  zoomInviteStatus: zoomInviteStatusSchema,
  zoomPersonalMeetingId: z.string().nullable(),
  isTrial: z.boolean().nullable(),
  qualityGrade: coachGradeSchema.nullable(),
  demoGrade: coachGradeSchema.nullable(),
  behaviorNotes: z.string().nullable(),
  historicalReviewNotes: z.string().nullable(),
  smsReminderOptedIn: z.boolean(),
  displayName: z.string().nullable(),
  secondaryEmail: z.string().nullable(),
  needsReview: z.boolean().nullable(),
  status: coachStatusSchema.nullable(),
  subStatuses: z.array(coachSubStatusSchema).nullable(),
  JPSGoogleMeetUrl: z.string().nullable(),
  airReadingGoogleMeetUrl: z.string().nullable(),
  zoomTechCheck: z.boolean().nullable(),
  googleMeetTechCheck: z.boolean().nullable(),
  backgroundCheckStatuses: z.array(backgroundCheckStatusSchema).nullable(),
  classEligibilities: z.array(classEligibilitySchema).nullable(),
  attendanceGrade: coachAttendanceGradeSchema.nullable(),
  overallGrade: coachOverallGradeSchema.nullable(),
  vendorId: z.string().nullable(),
  paymentInstrumentId: z.string().nullable(),
  paymentAccountType: z.lazy(() => paymentAccountType.nullable()),
  floridaTutorId: z.string().optional().nullable(),
  state: coachRegionSchema.nullable(),
  referralCode: z.string().nullable(),
  teachingPreferences: z.array(teachingPreferenceSchema).nullable(),
  documentsReviewPassed: z.boolean(),
  workshopPassed: z.boolean(),
  onboardingTechCheckPassed: z.boolean(),
  qualityGradeUpdatedAt: z.string().nullable(),
});
export type Coach = z.infer<typeof coachSchema>;

export const coachUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  fullNameOrEmail: z.string().nullable(),
  timeZone: z.string().nullable(),
  phone: z.string().nullable(),
  overallGrade: coachOverallGradeSchema.nullable(),
  vendorId: z.string().nullable(),
  paymentAccountType: z.lazy(() => paymentAccountType.nullable()),
  paymentInstrumentId: z.string().nullable(),
});
export type CoachUser = z.infer<typeof coachUserSchema>;

export const coachUserLikeSchema = z.object({
  id: z.string(),
  fullNameOrEmail: z.string().nullable(),
  qualityGrade: coachGradeSchema.nullable(),
  attendanceGrade: coachAttendanceGradeSchema.nullable(),
  status: coachStatusSchema.nullable(),
  subStatuses: z.array(coachSubStatusSchema).nullable(),
  zoomTechCheck: z.boolean().nullable(),
  googleMeetTechCheck: z.boolean().nullable(),
  backgroundCheckStatuses: z.array(backgroundCheckStatusSchema).nullable(),
  teachingPreferences: z.array(teachingPreferenceSchema).nullable(),
  classEligibilities: z.array(classEligibilitySchema).nullable(),
  overallGrade: coachOverallGradeSchema.nullable(),
});
export type CoachUserLike = z.infer<typeof coachUserLikeSchema>;

export const fullCoachSchema = coachUserSchema.merge(coachSchema).extend({
  coachName: z.string(),
  studentGroupCount: z.number().nullable(),
  tnsCountPast15Days: z.number().nullable(),
  subRequestCountPast15Days: z.number().nullable(),
  isEdgeCoach: z.boolean(),
  basicInfoCompleted: z.boolean(),
});
export type FullCoach = z.infer<typeof fullCoachSchema>;

export const coachOnboardingIncompleteStepSchema = z.enum([
  'basic_info_pending',
  'documents_pending',
  'workshop_pending',
  'tech_check_pending',
]);
export type CoachOnboardingIncompleteStep = z.infer<typeof coachOnboardingIncompleteStepSchema>;
export const CoachOnboardingIncompleteStep = coachOnboardingIncompleteStepSchema.enum;

export const CoachOnboardingIncompleteStepLabelMap: Record<CoachOnboardingIncompleteStep, string> = {
  [CoachOnboardingIncompleteStep.basic_info_pending]: 'Basic info pending',
  [CoachOnboardingIncompleteStep.documents_pending]: 'Documents pending',
  [CoachOnboardingIncompleteStep.workshop_pending]: 'Workshop pending',
  [CoachOnboardingIncompleteStep.tech_check_pending]: 'Tech check pending',
};

export const coachOnboardingRowSchema = fullCoachSchema;
export type CoachOnboardingRow = z.infer<typeof coachOnboardingRowSchema>;

export const eligibleCoachForClassTimeSchema = z.object({
  id: z.string(),
  fullNameOrEmail: z.string().nullable(),
  overallGrade: coachOverallGradeSchema.nullable(),
  conflictType: z.string().nullable(),
  conflictAmount: z.number().nullable(),
  hasGoogleMeetLink: z.boolean().optional(),
  sessions: z.array(plainSessionLikeSchema).optional(),
  substituteCoachSessions: z.array(plainSessionLikeSchema).optional(),
  studentGroupId: z.string().optional(),
});
export type EligibleCoachForClassTime = z.infer<typeof eligibleCoachForClassTimeSchema>;

export const coachWithStudentGroupsSchema = coachSchema.extend({
  studentGroups: z.array(studentGroupWithClassTimeSchema),
});
export type CoachWithStudentGroups = z.infer<typeof coachWithStudentGroupsSchema>;

export const updateCoachOptionsSchema = z.object({
  zoomPersonalMeetingUrl: z.string().nullable().optional(),
  zoomAccountId: z.string().optional(),
  isTrial: z.boolean().optional(),
  qualityGrade: coachGradeSchema.optional(),
  demoGrade: coachGradeSchema.optional(),
  attendanceGrade: coachAttendanceGradeSchema.optional(),
  behaviorNotes: z.string().optional(),
  historicalReviewNotes: z.string().nullable().optional(),
  zoomType: zoomTypeSchema.nullable().optional(),
  zoomPersonalMeetingId: z.string().nullable().optional(),
  zoomInviteStatus: zoomInviteStatusSchema.optional(),
  smsReminderOptedIn: z.boolean().optional(),
  displayName: z.string().optional(),
  secondaryEmail: z.string().nullable().optional(),
  needsReview: z.boolean().optional(),
  JPSGoogleMeetUrl: z.string().nullable().optional(),
  airReadingGoogleMeetUrl: z.string().nullable().optional(),
  zoomTechCheck: z.boolean().optional(),
  googleMeetTechCheck: z.boolean().optional(),
  backgroundCheckStatuses: z.array(backgroundCheckStatusSchema).optional(),
  classEligibilities: z.array(classEligibilitySchema).optional(),
  status: coachStatusSchema.optional(),
  subStatuses: z.array(coachSubStatusSchema).optional(),
  vendorId: z.string().nullable().optional(),
  paymentInstrumentId: z.string().nullable().optional(),
  paymentAccountType: z.lazy(() => paymentAccountType.nullable().optional()),
  floridaTutorId: z.string().optional().nullable(),
  state: coachRegionSchema.nullable().optional(),
  documentsReviewPassed: z.boolean().optional(),
  documentsLastUploadedAt: z.coerce.date().nullable().optional(),
  workshopPassed: z.boolean().optional(),
  onboardingTechCheckPassed: z.boolean().optional(),
});
export type UpdateCoachOptions = z.infer<typeof updateCoachOptionsSchema>;

export const recalculateAttendanceRequestSchema = z.object({
  coachId: z.string().optional(),
});
export type RecalculateAttendanceRequest = z.infer<typeof recalculateAttendanceRequestSchema>;

export const getCoachesSortableColumnSchema = fullCoachSchema
  .keyof()
  .extract([
    'email',
    'fullNameOrEmail',
    'qualityGrade',
    'attendanceGrade',
    'status',
    'needsReview',
    'overallGrade',
    'studentGroupCount',
  ]);
export type GetCoachesSortableColumn = z.infer<typeof getCoachesSortableColumnSchema>;

export const getCoachSortingSchema = sortingSchemaFrom(getCoachesSortableColumnSchema);

export const getCoachesSortOrderSchema = sortOrderSchemaFrom(getCoachesSortableColumnSchema);
export type GetCoachesSortOrder = z.infer<typeof getCoachesSortOrderSchema>;

export const getCoachesFilterSchema = z.object({
  query: z.string().optional(),
  overallGrades: z.array(coachOverallGradeSchema).optional(),
  teachingGrades: z.array(coachGradeSchema).optional(),
  attendanceGrades: z.array(coachAttendanceGradeSchema).optional(),
  statuses: z.array(coachStatusSchema).optional(),
  subStatuses: z.array(coachSubStatusSchema).optional(),
  availabilities: coachAvailabilityFromFilterSchema.optional(),
  classEligibilities: z.array(classEligibilitySchema).optional(),
  incompleteOnboardingSteps: z.array(coachOnboardingIncompleteStepSchema).optional(),
});
export type GetCoachesFilter = z.infer<typeof getCoachesFilterSchema>;

export const getCoachesModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getCoachesFilterSchema))
  .merge(getCoachSortingSchema);
export type GetCoachesModifier = z.infer<typeof getCoachesModifierSchema>;

export const getSubSessionTeachersModifierSchema = z.object({
  studentGroupId: z.string(),
  startDate: z.string().transform((s) => Temporal.PlainDate.from(s)),
  endDate: z.string().transform((s) => Temporal.PlainDate.from(s)),
});
export type GetSubSessionTeachersModifier = z.infer<typeof getSubSessionTeachersModifierSchema>;

export const getSubSessionTeachersResultsSchema = z.object({
  coaches: z.array(
    eligibleCoachForClassTimeSchema.extend({
      teachingPreferences: z.array(teachingPreferenceSchema),
      qualityGrade: coachGradeSchema.nullable(),
      attendanceGrade: coachAttendanceGradeSchema.nullable(),
      backgroundCheckStatuses: z.array(backgroundCheckStatusSchema),
      weeklySubsCount: z.number().nullable(),
    }),
  ),
});
export type GetSubSessionTeachersResults = z.infer<typeof getSubSessionTeachersResultsSchema>;

export const getCoachesResultsSchema = paginatedResponse.extend({
  coaches: z.array(fullCoachSchema),
});
export type GetCoachesResults = z.infer<typeof getCoachesResultsSchema>;

export const timeRangeSchema = z.object({
  day: dayOfWeekSchema,
  start: z.string(),
  end: z.string(),
});
export type TimeRange = z.infer<typeof timeRangeSchema>;

export const getCoachesAvailabilitiesQuerySchema = z.object({
  query: z.string().optional(),
  statuses: z.array(coachStatusSchema).optional(),
  subStatuses: z.array(coachSubStatusSchema).optional(),
  overallGrades: z.array(coachOverallGradeSchema).optional(),
  classEligibilities: z.array(classEligibilitySchema).optional(),
  teachingDays: z.array(dayOfWeekSchema).optional(),
  backgroundCheckStatuses: z.array(backgroundCheckStatusSchema).optional(),
  backgroundCheckStatusesLogicalOperator: logicalOperatorSchema.optional(),
  googleMeetTechCheck: z.boolean().optional(),
  hasAvailability: z.boolean().optional(),
  timeRanges: z.array(timeRangeSchema).optional(),
  untakenTeachersOnly: z.boolean().optional(),
  coachAvailabilityType: coachAvailabilityTypeSchema.optional(),
  states: z.array(z.string().trim().min(1)).optional(),
});
export type GetCoachesAvailabilitiesQuery = z.infer<typeof getCoachesAvailabilitiesQuerySchema>;

export const getCoachesAvailabilitiesPaginatedQuerySchema = pagingSchema.extend(
  getCoachesAvailabilitiesQuerySchema.shape,
);
export type GetCoachesAvailabilitiesPaginatedQuery = z.infer<typeof getCoachesAvailabilitiesPaginatedQuerySchema>;

export const getCoachesAvailabilitiesModifierSchema = pagingSchema.merge(
  filteringSchemaFrom(getCoachesAvailabilitiesQuerySchema),
);
export type GetCoachesAvailabilitiesModifier = z.infer<typeof getCoachesAvailabilitiesModifierSchema>;

export const coachAvailabilityItemSchema = z.object({
  coach: coachUserLikeSchema,
  availabilities: z.array(coachAvailabilitySchema),
  scheduledSessions: z.array(plainSessionLikeSchema),
  scheduledClasses: z.array(plainSessionLikeSchema),
});
export type CoachAvailabilityItem = z.infer<typeof coachAvailabilityItemSchema>;

export const scheduledClassItemSchema = z.object({
  classTimes: z.string(),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  sessionsLeft: z.number(),
  studentGroupId: z.string(),
  schoolId: z.string(),
});
export type ScheduledClassItem = z.infer<typeof scheduledClassItemSchema>;

export const getCoachesAvailabilitiesResultsSchema = paginatedResponse.extend({
  coachAvailabilities: z.array(coachAvailabilityItemSchema),
});
export type GetCoachesAvailabilitiesResults = z.infer<typeof getCoachesAvailabilitiesResultsSchema>;

export const getCoachStatesResultsSchema = z.object({
  states: z.array(z.string()),
});
export type GetCoachStatesResults = z.infer<typeof getCoachStatesResultsSchema>;

export const availabilityOverviewTimeslotSchema = z.object({
  startPlainTime: z.string(),
  coachCount: z.number(),
  studentSeats: z.number(),
  enrolledStudentsNeedSchedule: z.number(),
  teacherDemandForecast: z.number(),
});
export type AvailabilityOverviewTimeslot = z.infer<typeof availabilityOverviewTimeslotSchema>;

export const availabilityOverviewDaySchema = z.object({
  day: dayOfWeekSchema,
  coachCount: z.number(),
  studentSeats: z.number(),
  enrolledStudentsNeedSchedule: z.number(),
  teacherDemandForecast: z.number(),
});
export type AvailabilityOverviewDay = z.infer<typeof availabilityOverviewDaySchema>;

export const availabilityOverviewBackgroundCheckSchema = z.object({
  status: backgroundCheckStatusSchema,
  coachCount: z.number(),
});
export type AvailabilityOverviewBackgroundCheck = z.infer<typeof availabilityOverviewBackgroundCheckSchema>;

export const availabilityOverviewTimeZoneSchema = z.object({
  timeZone: z.string().nullable(),
  coachCount: z.number(),
});
export type AvailabilityOverviewTimeZone = z.infer<typeof availabilityOverviewTimeZoneSchema>;

export const availabilityOverviewSeasonTotalSchema = z.object({
  cycle: z.string(),
  coachCount: z.number(),
});
export type AvailabilityOverviewSeasonTotal = z.infer<typeof availabilityOverviewSeasonTotalSchema>;

export const availabilityOverviewDistrictSchema = z.object({
  orgId: z.string(),
  district: z.string(),
  coachCount: z.number(),
  studentSeats: z.number(),
  enrolledStudentsNeedSchedule: z.number(),
  teacherDemandForecast: z.number(),
});
export type AvailabilityOverviewDistrict = z.infer<typeof availabilityOverviewDistrictSchema>;

export const getAvailabilityOverviewResultsSchema = z.object({
  timeslots: z.array(availabilityOverviewTimeslotSchema),
  daysOfWeek: z.array(availabilityOverviewDaySchema),
  backgroundCheckStatuses: z.array(availabilityOverviewBackgroundCheckSchema),
  timeZones: z.array(availabilityOverviewTimeZoneSchema),
  seasonTotals: z.array(availabilityOverviewSeasonTotalSchema),
  districts: z.array(availabilityOverviewDistrictSchema),
});
export type GetAvailabilityOverviewResults = z.infer<typeof getAvailabilityOverviewResultsSchema>;

/** Teacher : student class size ratio used by availability overview planning. */
export const classRatioSchema = z.enum(['1:2', '1:3']);
export type ClassRatio = z.infer<typeof classRatioSchema>;
export const ClassRatio = classRatioSchema.enum;

/** 15-minute CT slots used by the availability overview (06:00 through 19:45 start). */
export const AVAILABILITY_OVERVIEW_SLOT_INTERVAL_MINUTES = 15;
export const AVAILABILITY_OVERVIEW_SLOT_COUNT = 56;
export const AVAILABILITY_OVERVIEW_SLOT_START = '06:00:00';

/** Start PlainTime strings (`HH:mm:ss`) for each overview timeslot. */
export function getAvailabilityOverviewTimeSlotStarts(): string[] {
  const start = Temporal.PlainTime.from(AVAILABILITY_OVERVIEW_SLOT_START);
  return Array.from({ length: AVAILABILITY_OVERVIEW_SLOT_COUNT }, (_, i) =>
    start.add({ minutes: i * AVAILABILITY_OVERVIEW_SLOT_INTERVAL_MINUTES }).toString(),
  );
}

const availabilityOverviewTimeSlotStarts = new Set(getAvailabilityOverviewTimeSlotStarts());

/**
 * Accepts only the fixed overview slot starts, so the value is always safe to cast to a
 * Postgres `time` and the number of distinct slots stays bounded.
 */
const availabilityOverviewTimeSlotSchema = z.string().refine((value) => availabilityOverviewTimeSlotStarts.has(value), {
  message: 'Must be a valid time slot (HH:mm:ss)',
});

/** Parseable by `Temporal.PlainDate.from`, which the server relies on when building date bounds. */
const plainDateStringSchema = z.string().refine(
  (value) => {
    try {
      Temporal.PlainDate.from(value);
      return true;
    } catch {
      return false;
    }
  },
  { message: 'Must be a calendar date (YYYY-MM-DD)' },
);

/** Availability overview filters: coach availabilities filters plus an optional date window. */
export const getAvailabilityOverviewFilterSchema = getCoachesAvailabilitiesQuerySchema.extend({
  /** Inclusive PlainDate (YYYY-MM-DD) lower bound for the planning / untaken-session window. */
  startOnOrAfter: plainDateStringSchema.optional(),
  /** Inclusive PlainDate (YYYY-MM-DD) upper bound for the planning / untaken-session window. */
  endOnOrBefore: plainDateStringSchema.optional(),
  classRatio: classRatioSchema.optional(),
  /** Selected overview timeslot starts (`HH:mm:ss`), each representing a 15-minute CT block. */
  timeSlots: z.array(availabilityOverviewTimeSlotSchema).max(AVAILABILITY_OVERVIEW_SLOT_COUNT).optional(),
});
export type GetAvailabilityOverviewFilter = z.infer<typeof getAvailabilityOverviewFilterSchema>;

export const getAvailabilityOverviewModifierSchema = filteringSchemaFrom(getAvailabilityOverviewFilterSchema);
export type GetAvailabilityOverviewModifier = z.infer<typeof getAvailabilityOverviewModifierSchema>;

/** Coach filters surfaced as URL search params on the availability overview page. */
export const availabilityOverviewSearchSchema = getAvailabilityOverviewFilterSchema.pick({
  statuses: true,
  subStatuses: true,
  overallGrades: true,
  classEligibilities: true,
  teachingDays: true,
  timeRanges: true,
  untakenTeachersOnly: true,
  coachAvailabilityType: true,
  googleMeetTechCheck: true,
  startOnOrAfter: true,
  endOnOrBefore: true,
  classRatio: true,
  timeSlots: true,
  states: true,
});
export type AvailabilityOverviewSearch = z.infer<typeof availabilityOverviewSearchSchema>;

export const getCoachGoogleMeetUrlForOrg = (
  coach: {
    JPSGoogleMeetUrl: string | null;
    airReadingGoogleMeetUrl: string | null;
  },
  orgName: string,
) => {
  if (orgName === OrganizationMatchNames.JPS) {
    return coach.JPSGoogleMeetUrl;
  }
  return coach.airReadingGoogleMeetUrl;
};

export const coachMissingZoomLicense = fullCoachSchema.extend({
  sessions: z.array(
    z.object({
      startInstant: z.instanceof(Temporal.Instant),
      endInstant: z.instanceof(Temporal.Instant),
    }),
  ),
});

export type CoachMissingZoomLicense = z.infer<typeof coachMissingZoomLicense>;

export const getCoachesMissingZoomLicenseSchema = paginatedResponse.extend({
  coaches: z.array(coachMissingZoomLicense),
});

export type GetCoachesMissingZoomLicenseResult = z.infer<typeof getCoachesMissingZoomLicenseSchema>;

export const coachMissingGoogleMeetSchema = z.object({
  id: z.string(),
  fullNameOrEmail: z.string().nullable(),
  email: z.string(),
  nextGoogleMeetSession: z.object({
    id: z.string(),
    startInstant: z.instanceof(Temporal.Instant),
    schoolName: z.string(),
    districtName: z.string(),
  }),
});
export type CoachMissingGoogleMeet = z.infer<typeof coachMissingGoogleMeetSchema>;

export const getCoachesMissingGoogleMeetSortableColumnSchema = z.enum(['fullNameOrEmail', 'nextGoogleMeetSessionDate']);
export type GetCoachesMissingGoogleMeetSortableColumn = z.infer<typeof getCoachesMissingGoogleMeetSortableColumnSchema>;

export const getCoachesMissingGoogleMeetSortOrderSchema = sortOrderSchemaFrom(
  getCoachesMissingGoogleMeetSortableColumnSchema,
);

const getCoachesMissingGoogleMeetSortingSchema = sortingSchemaFrom(getCoachesMissingGoogleMeetSortableColumnSchema);

const getCoachesMissingGoogleMeetFilterSchema = z.object({
  query: z.string().optional(),
});

export const getCoachesMissingGoogleMeetModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getCoachesMissingGoogleMeetFilterSchema))
  .merge(getCoachesMissingGoogleMeetSortingSchema);
export type GetCoachesMissingGoogleMeetModifier = z.infer<typeof getCoachesMissingGoogleMeetModifierSchema>;

export const getCoachesMissingGoogleMeetSchema = paginatedResponse.extend({
  coaches: z.array(coachMissingGoogleMeetSchema),
});
export type GetCoachesMissingGoogleMeetResult = z.infer<typeof getCoachesMissingGoogleMeetSchema>;

export type TechCheckType = z.infer<typeof techCheckTypeSchema>;
export const TechCheckType = techCheckTypeSchema.enum;
export const TechCheckTypeLabelMap: Record<TechCheckType, string> = {
  [TechCheckType.zoomTechCheck]: 'Zoom tech check',
  [TechCheckType.googleMeetTechCheck]: 'Google Meet tech check',
};

// ─── Coach TQ Review (Review Notes) ────────────────────────────────────────────

export const coachTqReviewTypeSchema = z.enum([
  'QR_New_First',
  'FR_New_Second',
  'FR_Current',
  'QR_Return',
  'Assessment',
]);
export type CoachTqReviewType = z.infer<typeof coachTqReviewTypeSchema>;
export const CoachTqReviewType = coachTqReviewTypeSchema.enum;

export const CoachTqReviewTypeLabelMap: Record<CoachTqReviewType, string> = {
  [CoachTqReviewType.QR_New_First]: 'QR - New (1st)',
  [CoachTqReviewType.FR_New_Second]: 'FR - New (2nd)',
  [CoachTqReviewType.FR_Current]: 'FR - Current',
  [CoachTqReviewType.QR_Return]: 'QR - Return',
  [CoachTqReviewType.Assessment]: 'Assessment',
};

export const coachTqAssessmentRepeatActionSchema = z.enum(['none', 'request', 'waiting']);
export type CoachTqAssessmentRepeatAction = z.infer<typeof coachTqAssessmentRepeatActionSchema>;
export const CoachTqAssessmentRepeatAction = coachTqAssessmentRepeatActionSchema.enum;

export const coachTqReviewStatusSchema = z.enum(['blank', 'in_progress', 'done']);
export type CoachTqReviewStatus = z.infer<typeof coachTqReviewStatusSchema>;
export const CoachTqReviewStatus = coachTqReviewStatusSchema.enum;

export const CoachTqReviewStatusLabelMap: Record<CoachTqReviewStatus, string> = {
  [CoachTqReviewStatus.blank]: 'Blank',
  [CoachTqReviewStatus.in_progress]: 'In progress',
  [CoachTqReviewStatus.done]: 'Done',
};

export const coachTqReviewSchema = z.object({
  id: z.string(),
  coachId: z.string(),
  reviewType: coachTqReviewTypeSchema,
  status: coachTqReviewStatusSchema,
  reviewDate: z
    .string()
    .refine((val) => {
      try {
        Temporal.Instant.from(val);
        return true;
      } catch {
        return false;
      }
    })
    .nullable(),
  reviewGrade: coachGradeSchema.nullable(),
  notes: z.string().nullable(),
  sessionId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  assessmentRepeatAction: coachTqAssessmentRepeatActionSchema,
});

export type CoachTqReview = z.infer<typeof coachTqReviewSchema>;

// ─── Coach TQ Review (centralized admin table) ─────────────────────────────────

export const coachTqReviewStudentSchema = z.object({
  id: z.string(),
  name: z.string(),
  specialEd: z.boolean(),
  dyslexic: z.boolean(),
  ell: z.boolean(),
});
export type CoachTqReviewStudent = z.infer<typeof coachTqReviewStudentSchema>;

export const coachTqReviewHistoricalDateSchema = z.object({
  reviewInstant: z.instanceof(Temporal.Instant),
  reviewType: coachTqReviewTypeSchema,
});
export type CoachTqReviewHistoricalDate = z.infer<typeof coachTqReviewHistoricalDateSchema>;

export const coachTqReviewItemSchema = z.object({
  id: z.string(),
  coachId: z.string(),
  teacherName: z.string().nullable(),
  teachingGrade: coachGradeSchema.nullable(),
  reviewType: coachTqReviewTypeSchema,
  status: coachTqReviewStatusSchema,
  /** Completion timestamp when status is done; null otherwise. */
  reviewInstant: z.instanceof(Temporal.Instant).nullable(),
  /** Most recent completed review date for this coach. */
  lastReviewInstant: z.instanceof(Temporal.Instant).nullable(),
  historicalReviews: z.array(coachTqReviewHistoricalDateSchema),
  mostRecentSessionId: z.string().nullable(),
  mostRecentSessionInstant: z.instanceof(Temporal.Instant).nullable(),
  meetingType: videoConferenceTypeSchema.nullable(),
  recordingUrls: z.array(z.string()),
  lessonId: z.string().nullable(),
  lessonName: z.string().nullable(),
  students: z.array(coachTqReviewStudentSchema),
  /**
   * Class notes from all student feedbacks for the most recent session.
   * Format: "[Student name] notes" per student, joined by newlines. Null when empty.
   */
  classNotes: z.string().nullable(),
  /** Count of unsubmitted student feedbacks for this coach in the past 30 days. */
  missingFeedbackCount: z.number(),
  activeStudentGroupCount: z.number(),
  totalFutureSessions: z.number(),
});
export type CoachTqReviewItem = z.infer<typeof coachTqReviewItemSchema>;

export const getCoachesTqReviewSortableColumnSchema = z.enum(['lastReviewDate']);
export type GetCoachesTqReviewSortableColumn = z.infer<typeof getCoachesTqReviewSortableColumnSchema>;

export const getCoachesTqReviewSortOrderSchema = sortOrderSchemaFrom(getCoachesTqReviewSortableColumnSchema);

const getCoachesTqReviewSortingSchema = sortingSchemaFrom(getCoachesTqReviewSortableColumnSchema);

const getCoachesTqReviewFilterSchema = z.object({
  query: z.string().optional(),
  reviewTypes: z.array(coachTqReviewTypeSchema).optional(),
});

export const getCoachesTqReviewModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getCoachesTqReviewFilterSchema))
  .merge(getCoachesTqReviewSortingSchema);
export type GetCoachesTqReviewModifier = z.infer<typeof getCoachesTqReviewModifierSchema>;

export const getCoachesTqReviewResultSchema = paginatedResponse.extend({
  reviews: z.array(coachTqReviewItemSchema),
});
export type GetCoachesTqReviewResult = z.infer<typeof getCoachesTqReviewResultSchema>;

export const updateCoachTqReviewStatusSchema = z.object({
  status: coachTqReviewStatusSchema,
});
export type UpdateCoachTqReviewStatus = z.infer<typeof updateCoachTqReviewStatusSchema>;

export const createCoachTqReviewSchema = z.object({
  coachId: z.string().uuid(),
  reviewType: coachTqReviewTypeSchema,
  status: coachTqReviewStatusSchema.optional(),
});
export type CreateCoachTqReview = z.infer<typeof createCoachTqReviewSchema>;

// ─── Google Meet TQ Review ────────────────────────────────────────────────────

export const googleMeetEvaluationStatusSchema = z.enum(['send_message', 'no_action_needed']);
export type GoogleMeetEvaluationStatus = z.infer<typeof googleMeetEvaluationStatusSchema>;
export const GoogleMeetEvaluationStatus = googleMeetEvaluationStatusSchema.enum;
export const GoogleMeetEvaluationStatusLabelMap: Record<GoogleMeetEvaluationStatus, string> = {
  [GoogleMeetEvaluationStatus.send_message]: 'Send message',
  [GoogleMeetEvaluationStatus.no_action_needed]: 'No action needed',
};

export const coachGoogleMeetTqReviewSchema = z.object({
  id: z.string(),
  fullNameOrEmail: z.string().nullable(),
  email: z.string(),
  nextGoogleMeetSession: z
    .object({
      startInstant: z.instanceof(Temporal.Instant),
      classTime: z.object({
        type: classTimeTypeSchema,
        meetingBlocks: z.array(
          z.object({
            day: dayOfWeekSchema,
            startPlainTime: z.string(),
            endPlainTime: z.string(),
          }),
        ),
      }),
    })
    .nullable(),
  googleMeetNotes: z.string().nullable(),
  googleMeetEvaluationStatus: googleMeetEvaluationStatusSchema.nullable(),
  JPSGoogleMeetUrl: z.string().nullable(),
  airReadingGoogleMeetUrl: z.string().nullable(),
});
export type CoachGoogleMeetTqReview = z.infer<typeof coachGoogleMeetTqReviewSchema>;

export const getCoachesGoogleMeetTqReviewSortableColumnSchema = z.enum([
  'fullNameOrEmail',
  'nextGoogleMeetSessionDate',
  'googleMeetEvaluationStatus',
]);
export type GetCoachesGoogleMeetTqReviewSortableColumn = z.infer<
  typeof getCoachesGoogleMeetTqReviewSortableColumnSchema
>;

export const getCoachesGoogleMeetTqReviewSortOrderSchema = sortOrderSchemaFrom(
  getCoachesGoogleMeetTqReviewSortableColumnSchema,
);

const getCoachesGoogleMeetTqReviewSortingSchema = sortingSchemaFrom(getCoachesGoogleMeetTqReviewSortableColumnSchema);

const getCoachesGoogleMeetTqReviewFilterSchema = z.object({
  query: z.string().optional(),
  evaluationStatuses: z.array(googleMeetEvaluationStatusSchema).optional(),
});

export const getCoachesGoogleMeetTqReviewModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getCoachesGoogleMeetTqReviewFilterSchema))
  .merge(getCoachesGoogleMeetTqReviewSortingSchema);
export type GetCoachesGoogleMeetTqReviewModifier = z.infer<typeof getCoachesGoogleMeetTqReviewModifierSchema>;

export const getCoachesGoogleMeetTqReviewResultSchema = paginatedResponse.extend({
  coaches: z.array(coachGoogleMeetTqReviewSchema),
});
export type GetCoachesGoogleMeetTqReviewResult = z.infer<typeof getCoachesGoogleMeetTqReviewResultSchema>;

export const updateCoachGoogleMeetTqReviewSchema = z.object({
  googleMeetNotes: z.string().nullable().optional(),
  googleMeetEvaluationStatus: googleMeetEvaluationStatusSchema.nullable().optional(),
});
export type UpdateCoachGoogleMeetTqReview = z.infer<typeof updateCoachGoogleMeetTqReviewSchema>;
